import * as React from 'react';
import { alpha, keyframes, styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAuth } from '../auth/AuthContext';
import { CustomIcon } from './AppNavbar';
import SelectContent from './SelectContent';
import MenuContent from './MenuContent';
// import CardAlert from './CardAlert';
import OptionsMenu from './OptionsMenu';

const livePulse = keyframes`
  0% { transform: scale(1); opacity: 0.9; }
  70% { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(2.2); opacity: 0; }
`;

const drawerWidth = 240;

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  mt: 10,
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: 'border-box',
  },
});

export default function SideMenu() {
  const { session } = useAuth();

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', md: 'block' },
        [`& .${drawerClasses.paper}`]: {
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundImage: 'none',
          backdropFilter: 'blur(18px)',
          background: (theme) => theme.vars
            ? `linear-gradient(180deg, rgba(${theme.vars.palette.background.paperChannel} / 0.74) 0%, rgba(${theme.vars.palette.background.defaultChannel} / 0.88) 100%)`
            : `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.74)} 0%, ${alpha(theme.palette.background.default, 0.88)} 100%)`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          mt: 'calc(var(--template-frame-height, 0px) + 4px)',
          p: 2,
        }}
      >
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', px: 0.5 }}>
          <CustomIcon />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2">EV Intelligence</Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              Premium analytics workspace
            </Typography>
          </Box>
          <Chip
            size="small"
            label="Live"
            icon={
              <Box
                sx={(theme) => ({
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#36b82a',
                  position: 'relative',
                  boxShadow: `0 0 0 1px ${alpha(theme.palette.common.black, 0.18)}`,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -3,
                    borderRadius: '50%',
                    backgroundColor: alpha('#36b82a', 0.55),
                    animation: `${livePulse} 1.6s ease-out infinite`,
                  },
                })}
              />
            }
            sx={(theme) => ({
              borderRadius: 999,
              px: 0.35,
              fontWeight: 700,
              border: '1px solid',
              borderColor: alpha('#36b82a', 0.45),
              color: '#d7ffe2',
              background: `linear-gradient(180deg, ${alpha('#36b82a', 0.25)} 0%, ${alpha('#1f7f28', 0.25)} 100%)`,
              '& .MuiChip-icon': {
                ml: 0.3,
                mr: -0.2,
              },
            })}
          />
        </Stack>
        <SelectContent />
      </Box>
      <Divider />
      <MenuContent />
      {/* <CardAlert /> */}
      <Stack
        direction="row"
        sx={{
          p: 2,
          gap: 1,
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Avatar
          sizes="small"
          alt="Admin"
          src="/favicon.svg"
          sx={(theme) => ({
            width: 40,
            height: 40,
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.9),
            boxShadow: `0 10px 24px ${alpha(theme.palette.common.black, 0.12)}`,
          })}
        />
        <Box sx={{ mr: 'auto' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: '16px' }}>
            Dashboard User
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {session?.userId || 'Guest'}
          </Typography>
        </Box>
        <OptionsMenu />
      </Stack>
    </Drawer>
  );
}
